import { isAfter, isBefore } from 'date-fns';
import { useUpdateTask } from 'features/tasks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar16, CalendarDeleteDate16 } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import {
  deletePlannedOccurrenceForDate,
  getScheduleEndDate,
} from 'shared/utils/schedules';
import { MetaViewOptions } from 'shared/views/task-detail';

export type TimeslotListProps = {
  task: Task;
  date: Timestamp;
  id?: ID;
  onClose: () => void;
  onChangeTimeslot?: () => void;
  onAction?: () => void;
};

export const TimeslotList: React.FC<TimeslotListProps> = ({
  task,
  id,
  date,
  onClose,
  onChangeTimeslot,
  onAction,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { submit: updateTask } = useUpdateTask();
  const openTaskDetail = useOpenTaskDetail();

  const onReschedule = () => {
    openTaskDetail(task, MetaViewOptions.Upcoming);
    onAction?.();
    onClose();
  };

  const onRemoveTimeslot = () => {
    // regular tasks do not have multiple timeslots, so we can close the menu
    if (task.type === TaskType.Task) {
      onClose();
      return;
    }

    const schedules = task.schedules
      ?.toSorted(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      )
      .map((schedule, index, arr) => {
        const prevScheduleEndDate = getScheduleEndDate(arr[index - 1]);
        const scheduleEndDate = getScheduleEndDate(schedule);

        if (
          (prevScheduleEndDate && isBefore(date, prevScheduleEndDate)) ||
          (scheduleEndDate && isAfter(date, scheduleEndDate))
        ) {
          return schedule;
        }

        return deletePlannedOccurrenceForDate(schedule, date, {
          weekStartsOn,
          id: id,
          today,
        });
      });

    // update the schedule with a `timeslot` based on the event
    updateTask({ id: task.id, schedules });
    onClose();
  };

  return task.type !== TaskType.Task ? (
    <PopupMenuList hasBottomBorder>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onReschedule}
          start={<Icon icon={Calendar16} />}
        >
          {t('context-menus.task.labels.timeslot.occurrence.reschedule')}
        </PopupMenuButton>
      </PopupMenuListItem>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onRemoveTimeslot}
          start={<Icon icon={CalendarDeleteDate16} />}
        >
          {t('context-menus.task.labels.timeslot.occurrence.remove')}
        </PopupMenuButton>
      </PopupMenuListItem>
      {/* {!!onChangeTimeslot && (
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onChangeTimeslot}
            start={<Icon icon={ClockWatch16} />}
          >
            {t('context-menus.task.labels.timeslot.occurrence.change-time')}
          </PopupMenuButton>
        </PopupMenuListItem>
      )} */}
    </PopupMenuList>
  ) : null;
};
